/**
 * components/DnaCompactCard.js — Phase 74B dashboard tile.
 *
 * A dense 4-column grid of the 8 Financial DNA attributes. Each tile
 * shows: short label, tone dot, score chip. "View your profile" opens the
 * deep-dive page.
 *
 * Data comes from `snapshot.dna.attributes` (populated by the backend in
 * routes/snapshot.py). The dashboard already caches the snapshot in memory
 * via useSnapshot() — this component is a pure view.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Fingerprint, ArrowRight } from 'lucide-react';

const TONE_COLORS = {
  strong:   'bg-emerald-500',
  building: 'bg-amber-500',
  watch:    'bg-rose-500',
  muted:    'bg-muted-foreground/30',
};

const TONE_TEXT = {
  strong:   'text-emerald-700 dark:text-emerald-400',
  building: 'text-amber-700 dark:text-amber-400',
  watch:    'text-rose-700 dark:text-rose-400',
  muted:    'text-muted-foreground',
};

function ScoreChip({ score, tone }) {
  if (score == null) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <span className={`text-xs font-semibold ${TONE_TEXT[tone] || 'text-foreground'}`}>
      {Math.round(score)}
    </span>
  );
}

export function DnaCompactCard({ dna, loading }) {
  if (loading) {
    return (
      <Card className="rounded-2xl border-border/70" data-testid="dna-compact-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Fingerprint className="h-4 w-4 text-primary" />
              <div className="text-sm font-medium">Your Financial DNA</div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-16 rounded-xl" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const attrs = dna?.attributes || [];
  const overall = dna?.overall_score;
  const overallTone = dna?.overall_tone || 'muted';

  return (
    <Card className="rounded-2xl border-border/70" data-testid="dna-compact-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Fingerprint className="h-4 w-4 text-primary" />
            <div className="text-sm font-medium">Your Financial DNA</div>
            {overall != null && (
              <span className={`text-xs ml-2 ${TONE_TEXT[overallTone]}`} data-testid="dna-overall-score">
                overall {Math.round(overall)}
              </span>
            )}
          </div>
          <Link
            to="/financial-dna"
            className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 inline-flex items-center gap-1"
            data-testid="dna-see-profile"
          >
            See DNA breakdown <ArrowRight className="h-3 w-3" />
          </Link>
        </div>

        {attrs.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4">
            Your DNA takes shape as you add transactions and fill in your Life Profile.
          </div>
        ) : (
          <TooltipProvider delayDuration={200}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="dna-grid">
              {attrs.map((a) => (
                <Tooltip key={a.key}>
                  <TooltipTrigger asChild>
                    <div
                      className="rounded-xl border border-border/60 bg-muted/20 hover:bg-muted/40 transition-colors p-3 min-h-[76px] flex flex-col justify-between"
                      data-testid={`dna-tile-${a.key}`}
                    >
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`inline-block h-1.5 w-1.5 rounded-full ${TONE_COLORS[a.tone] || TONE_COLORS.muted}`}
                          aria-hidden
                        />
                        <span className="text-[11px] uppercase tracking-wide text-muted-foreground truncate">
                          {a.label}
                        </span>
                      </div>
                      <div className="mt-1 flex items-baseline gap-1.5">
                        <ScoreChip score={a.score} tone={a.tone} />
                        <span className={`text-[10px] ${TONE_TEXT[a.tone]}`}>
                          {a.tone === 'muted' ? 'not enough data' : a.tone}
                        </span>
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <div className="text-xs">{a.descriptor}</div>
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          </TooltipProvider>
        )}
      </CardContent>
    </Card>
  );
}

export default DnaCompactCard;
